/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

// ---------------------------------------------------------------------------
// TypeScript interfaces (for converted .tsx consumers)
// ---------------------------------------------------------------------------

export interface User {
  id: number;
  url?: string;
  username: string;
  email?: string;
  first_name: string;
  last_name: string;
  user_role?: string;
  profile_picture?: string;
}

export interface Notification {
  id: number;
  url?: string;
  title: string;
  airspace: object;
  type: 'NORMAL' | 'ALARM';
  sensors?: unknown[];
  priority: object;
  devices: unknown[];
  status: 'read' | 'unread';
  created_at: string;
}

export interface DeviceAirspace {
  id: number;
  online: boolean;
  decks?: number[];
  name?: string;
  product_type?: string;
}

export interface Device {
  id: number;
  url?: string;
  name: string;
  last_update?: string;
  algo_type?: string;
  software_version: string;
  installation_date?: string;
  airspace?: DeviceAirspace;
  ip_address?: string;
  delivery_status: string;
  cough_status: string;
  temperature_status: string;
  humidity_status: string;
}

export interface AlertAirspaceRef {
  id: number | null;
  name: string | null;
  product_type: string | null;
}

export interface LegacyAlertItem {
  type: 'legacy';
  id: number;
  airspace: AlertAirspaceRef;
  severity: 'MEDIUM' | 'HIGH';
  created_at: string;
  title: string;
  sensors: string[];
  priority: 'Warning' | 'Alarm';
}

export type EvidenceState = 'true' | 'false' | 'unknown';

export interface GroupEvidenceNode {
  type: 'all' | 'any';
  state: EvidenceState;
  conditions: EvidenceNode[];
}

export interface MetricEvidenceNode {
  type: 'metric';
  metric: string;
  op: string;
  value: number;
  actual_value: number | null;
  state: EvidenceState;
}

export interface AttributeEvidenceNode {
  type: 'attribute';
  attr: string;
  op: string;
  value: number | string;
  actual_value: number | string | null;
  state: EvidenceState;
}

export interface StaleEvidenceNode {
  type: 'stale';
  metric: string;
  for_minutes: number;
  actual_minutes: number | null;
  state: EvidenceState;
}

export interface MortalityEvidenceNode {
  type: 'mortality';
  measure: string;
  window_days: number;
  op: string;
  value: number;
  actual_value: number | null;
  deaths?: number | null;
  max_stock?: number | null;
  state: EvidenceState;
}

export interface MortalityMissingEvidenceNode {
  type: 'mortality_missing';
  for_days: number;
  due_by_hour?: number;
  missing: boolean | null;
  date: string | null;
  state: EvidenceState;
}

export type AdministrativeReason = 'out_of_scope' | 'batch_closed' | 'rule_archived';

export interface AdministrativeEvidenceNode {
  type: 'administrative';
  reason: AdministrativeReason;
  state: EvidenceState;
}

export type EvidenceNode =
  | GroupEvidenceNode
  | MetricEvidenceNode
  | AttributeEvidenceNode
  | StaleEvidenceNode
  | MortalityEvidenceNode
  | MortalityMissingEvidenceNode
  | AdministrativeEvidenceNode;

export interface AlertEventEvidence {
  rule_version_id: number;
  rule_version: number;
  severity: string;
  airspace_id: number;
  rule_conditions: unknown;
  evaluation: EvidenceNode;
  sensor_data: Record<string, unknown>;
  airspace_attributes: Record<string, unknown>;
  evaluated_at: string;
}

export interface AlertEvent {
  type: 'OPENED' | 'RESOLVED';
  evaluated_at: string;
  // Full evaluation payload, or an administrative evidence node sent as `evidence` itself.
  evidence: AlertEventEvidence | AdministrativeEvidenceNode;
}

export interface CustomAlertItem {
  type: 'custom';
  id: number;
  airspace: AlertAirspaceRef;
  severity: 'INFO' | 'MEDIUM' | 'HIGH';
  state: 'ACTIVE' | 'RESOLVED';
  created_at: string;
  rule: { id: number; name: string; alert_name: string; version: number };
  opened_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  // The site-local day (YYYY-MM-DD) a per-day alert is about; "" for every other alert.
  subject_key: string;
  events: AlertEvent[];
}

export type AlertItem = LegacyAlertItem | CustomAlertItem;

export interface Message {
  id: number;
  user: number;
  note: number;
  message: string;
  created_at: string;
  username: string;
}

export interface NoteAuthor {
  id: number;
  url?: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface Note {
  id?: number;
  site?: number;
  url?: string;
  title?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  author?: NoteAuthor;
  status?: string;
  last_message?: string;
  last_message_time?: string;
  author_last_message?: User;
  messages?: Message[];
  profile_pictures?: Record<string, unknown>;
  recipients?: User[];
  last_read?: string;
}

export interface Site {
  name: string;
  longitude?: string;
  latitude?: string;
  last_update?: string;
  street?: string;
  house_number?: number;
  postal_code?: string;
  city?: string;
  country: string;
  current_user_role?: string;
  status: string;
  timezone: string;
  type?: string;
  url?: string;
  company?: number;
  latest_notifications: Notification[];
  unread_notifications: number;
  latest_alerts?: AlertItem[];
  unread_alerts?: number;
  unread_notes?: number;
  offline_count: number;
  product_types?: string[];
  show_last_update?: boolean;
  company_id?: number;
  preinstalled?: boolean;
  mortality_enabled?: boolean;
  multi_monitor_rehs_enabled?: boolean;
  doa_enabled?: boolean;
  recommendations?: number;
  subscription_tier?: string;
  weeks_on_batch_min?: number | null;
  weeks_on_batch_max?: number | null;
  mortality_last_7_days?: number | null;
  mortality_today?: number | null;
  mortality_rate_avg?: number | null;
  lowest_rhs_value?: number | null;
}

export interface Gateway {
  code: string;
  country?: string;
  device_mode?: string;
  device_mode_parameter?: string;
  last_update?: string;
  name?: string;
  software_version?: string;
  registration?: string;
  mac_address: string;
  site?: Site;
  devices_amount?: number;
  disk_space?: number;
  online: boolean;
}

export interface Repeater {
  code: string;
  last_update?: string;
  name?: string;
  software_version?: string;
  installation_date?: string;
  mac_address: string;
  site?: Site;
  online: boolean;
}

export interface WeatherInformation {
  temperature?: string;
  wind?: string;
  humidity?: string;
}

export interface SiteStatus {
  id: number;
  name: string;
  current_weather_information?: WeatherInformation;
  count_airspaces?: number;
  count_airspaces_offline?: number;
  count_devices?: number;
  count_devices_offline?: number;
  cough_alarm?: number;
  cough_warning?: number;
  temperature_alarm?: number;
  temperature_warning?: number;
  humidity_alarm?: number;
  humidity_warning?: number;
  status_url?: string;
}

export interface Group {
  name?: string;
  linked_sites?: Site[];
}

export interface SensorStatus {
  warning?: number;
  alarm?: number;
}

export interface Airspace {
  id: number;
  site_pk: number;
  name?: string;
  last_update?: string;
  product_type: string;
  day_in_episode?: number;
  outbreak_start?: unknown;
  recommendations?: number;
  coughs?: SensorStatus;
  humidity?: SensorStatus;
  temperature?: SensorStatus;
  days_without_issues?: number;
  status: string;
  color: string;
  outbreak_interrupted?: boolean;
}

export interface StatusCounts {
  ok?: number;
  warning?: number;
  alarm?: number;
}

export interface AirspaceOverview {
  rhs_status: StatusCounts;
  rhs_prediction?: unknown;
  thermal_shock_alerts_enabled: boolean;
  thermal_stress_alerts_enabled: boolean;
  environmental_status: StatusCounts;
}

export interface AirspaceStatus {
  id: number;
  name?: string;
  sensor: 0 | 1 | 2;
  last_update?: string;
  status: 'Unknown' | 'Warning' | 'Success' | 'Alarm';
}

export interface AirspaceHistoryEntry {
  datetime?: string;
  status?: number;
}

export interface AirspaceHistory {
  id: number;
  name?: string;
  sensor: 0 | 1 | 2;
  history?: AirspaceHistoryEntry[];
}

export interface Profile {
  id: number;
  usersettings_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  telephone?: string;
  language?: string;
  timezone?: string;
  temperature?: string;
  profile_picture?: string;
  country: string;
  paging_size?: number;
  note_switch?: boolean;
  notification_switch?: boolean;
  alerts_switch?: boolean;
  accepted_last_data_privacy?: boolean;
  accepted_last_privacy_policy?: boolean;
  consent_newsletter?: boolean;
  consent_personal_data_usage?: boolean;
}

export interface Rdi {
  data?: Record<string, unknown>[];
  id: number;
  name?: string;
}

export interface TemperatureHumidity {
  id: number;
  data?: {
    humidity?: Record<string, unknown>[];
    temperature?: Record<string, unknown>[];
  };
  name?: string;
}

export interface AirspacesStatuses {
  id: number;
  name?: string;
  data?: Record<string, unknown>[];
  devices?: Record<string, unknown>[];
}

export interface Rhs {
  id: number;
  fullname?: string;
  data?: Record<string, unknown>[];
}

export interface VetHistory {
  id: number;
  name?: string;
  sensor?: 0 | 1 | 2;
  devices?: Record<string, unknown>[];
}

export interface LogbookEvent {
  datetime?: unknown;
  type?: string;
  value?: Record<string, unknown>;
}

export interface LogbookItem {
  date?: unknown;
  events?: LogbookEvent[];
}

export interface ColorHistory {
  date?: unknown;
  color?: string;
  real_color?: string;
  status?: string;
  real_status?: string;
}

// ---------------------------------------------------------------------------
// Runtime PropTypes objects (kept for legacy .jsx consumers)
// ---------------------------------------------------------------------------

export const user = {
  id: PropTypes.number,
  url: PropTypes.string,
  username: PropTypes.string,
  email: PropTypes.string,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  user_role: PropTypes.string, // ENUM
  profile_picture: PropTypes.string,
};

export const notification = {
  id: PropTypes.number,
  url: PropTypes.string,
  title: PropTypes.string,
  airspace: PropTypes.object,
  type: PropTypes.oneOf(['NORMAL', 'ALARM']),
  sensors: PropTypes.array,
  priority: PropTypes.object,
  devices: PropTypes.array,
  status: PropTypes.oneOf(['read', 'unread']),
  created_at: PropTypes.string,
};

export const device = {
  id: PropTypes.number,
  url: PropTypes.string,
  name: PropTypes.string,
  last_update: PropTypes.string,
  algo_type: PropTypes.string,
  software_version: PropTypes.string,
  installation_date: PropTypes.string,
  airspace: ImmutablePropTypes.mapContains({
    id: PropTypes.number,
    online: PropTypes.bool,
    decks: ImmutablePropTypes.listOf(PropTypes.number),
  }),
  ip_address: PropTypes.string,
  delivery_status: PropTypes.string, // RECEIVED
  cough_status: PropTypes.string, // SAFE
  temperature_status: PropTypes.string, // SAFE
  humidity_status: PropTypes.string, // WARNING
};

export const alert = {
  type: PropTypes.oneOf(['legacy', 'custom']),
  id: PropTypes.number,
  airspace: ImmutablePropTypes.mapContains({
    id: PropTypes.number,
    name: PropTypes.string,
    product_type: PropTypes.string,
  }),
  severity: PropTypes.oneOf(['INFO', 'MEDIUM', 'HIGH']),
  created_at: PropTypes.string,
  // legacy-only
  title: PropTypes.string,
  sensors: ImmutablePropTypes.listOf(PropTypes.string),
  priority: PropTypes.oneOf(['Warning', 'Alarm']),
  // custom-only
  state: PropTypes.oneOf(['ACTIVE', 'RESOLVED']),
  rule: ImmutablePropTypes.mapContains({
    id: PropTypes.number,
    name: PropTypes.string,
    alert_name: PropTypes.string,
    version: PropTypes.number,
  }),
  opened_at: PropTypes.string,
  last_seen_at: PropTypes.string,
  resolved_at: PropTypes.string,
  subject_key: PropTypes.string,
};

export const message = {
  id: PropTypes.number,
  user: PropTypes.number,
  note: PropTypes.number,
  message: PropTypes.string,
  created_at: PropTypes.string,
  username: PropTypes.string,
};

export const note = {
  id: PropTypes.number,
  site: PropTypes.number,
  url: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
  author: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }),
  status: PropTypes.string,
  last_message: PropTypes.string,
  last_message_time: PropTypes.string,
  author_last_message: ImmutablePropTypes.mapContains(user),
  messages: ImmutablePropTypes.listOf(ImmutablePropTypes.mapContains(message)),
  profile_pictures: ImmutablePropTypes.map,
  recipients: ImmutablePropTypes.listOf(ImmutablePropTypes.mapContains(user)),
  last_read: PropTypes.string,
};

export const site = {
  name: PropTypes.string,
  longitude: PropTypes.string,
  latitude: PropTypes.string,
  last_update: PropTypes.string,
  street: PropTypes.string,
  house_number: PropTypes.number,
  postal_code: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  current_user_role: PropTypes.string,
  status: PropTypes.string,
  timezone: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  company: PropTypes.number,
  latest_notifications: ImmutablePropTypes.listOf(ImmutablePropTypes.mapContains(notification)),
  unread_notifications: PropTypes.number,
  latest_alerts: ImmutablePropTypes.listOf(ImmutablePropTypes.mapContains(alert)),
  unread_alerts: PropTypes.number,
  unread_notes: PropTypes.number,
  offline_count: PropTypes.number,
  product_types: PropTypes.arrayOf(PropTypes.string),
  show_last_update: PropTypes.bool,
  company_id: PropTypes.number,
  preinstalled: PropTypes.bool,
  mortality_enabled: PropTypes.bool,
  multi_monitor_rehs_enabled: PropTypes.bool,
  doa_enabled: PropTypes.bool,
  recommendations: PropTypes.number,
  subscription_tier: PropTypes.string,
  weeks_on_batch_min: PropTypes.number,
  weeks_on_batch_max: PropTypes.number,
  mortality_last_7_days: PropTypes.number,
  mortality_today: PropTypes.number,
  mortality_rate_avg: PropTypes.number,
  lowest_rhs_value: PropTypes.number,
};

export const gateway = {
  code: PropTypes.string,
  country: PropTypes.string,
  device_mode: PropTypes.string,
  device_mode_parameter: PropTypes.string,
  last_update: PropTypes.string,
  name: PropTypes.string,
  software_version: PropTypes.string,
  registration: PropTypes.string,
  mac_address: PropTypes.string,
  site: ImmutablePropTypes.mapContains(site),
  devices_amount: PropTypes.number,
  disk_space: PropTypes.number,
  online: PropTypes.bool,
};

export const repeater = {
  code: PropTypes.string,
  last_update: PropTypes.string,
  name: PropTypes.string,
  software_version: PropTypes.string,
  installation_date: PropTypes.string,
  mac_address: PropTypes.string,
  site: ImmutablePropTypes.mapContains(site),
  online: PropTypes.bool,
};

export const siteStatus = {
  id: PropTypes.number,
  name: PropTypes.string,
  current_weather_information: ImmutablePropTypes.mapContains({
    temperature: PropTypes.string,
    wind: PropTypes.string,
    humidity: PropTypes.string,
  }),
  count_airspaces: PropTypes.number,
  count_airspaces_offline: PropTypes.number,
  count_devices: PropTypes.number,
  count_devices_offline: PropTypes.number,
  cough_alarm: PropTypes.number,
  cough_warning: PropTypes.number,
  temperature_alarm: PropTypes.number,
  temperature_warning: PropTypes.number,
  humidity_alarm: PropTypes.number,
  humidity_warning: PropTypes.number,
  status_url: PropTypes.string,
};

export const group = {
  name: PropTypes.string,
  linked_sites: ImmutablePropTypes.listOf(ImmutablePropTypes.mapContains(site)),
};

export const airspace = {
  id: PropTypes.number,
  site_pk: PropTypes.number,
  name: PropTypes.string,
  last_update: PropTypes.string,
  product_type: PropTypes.string,
  day_in_episode: PropTypes.number,
  outbreak_start: PropTypes.any,
  recommendations: PropTypes.number,
  coughs: PropTypes.shape({
    warning: PropTypes.number,
    alarm: PropTypes.number,
  }),
  humidity: PropTypes.shape({
    warning: PropTypes.number,
    alarm: PropTypes.number,
  }),
  temperature: PropTypes.shape({
    warning: PropTypes.number,
    alarm: PropTypes.number,
  }),
  days_without_issues: PropTypes.number,
  status: PropTypes.string,
  color: PropTypes.string,
  outbreak_interrupted: PropTypes.bool,
};

export const airspaceOverview = {
  rhs_status: PropTypes.shape({
    ok: PropTypes.number,
    warning: PropTypes.number,
    alarm: PropTypes.number,
  }),
  rhs_prediction: PropTypes.any,
  thermal_shock_alerts_enabled: PropTypes.bool,
  thermal_stress_alerts_enabled: PropTypes.bool,
  environmental_status: PropTypes.shape({
    ok: PropTypes.number,
    warning: PropTypes.number,
    alarm: PropTypes.number,
  }),
};

export const airspaceStatus = {
  id: PropTypes.number,
  name: PropTypes.string,
  sensor: PropTypes.oneOf([0, 1, 2]),
  last_update: PropTypes.string,
  status: PropTypes.oneOf(['Unknown', 'Warning', 'Success', 'Alarm']),
};

export const airspaceHistory = {
  id: PropTypes.number,
  name: PropTypes.string,
  sensor: PropTypes.oneOf([0, 1, 2]),
  history: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      datetime: PropTypes.string,
      status: PropTypes.number,
    }),
  ),
};

export const profile = {
  id: PropTypes.number,
  usersettings_id: PropTypes.number,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  email: PropTypes.string,
  telephone: PropTypes.string,
  language: PropTypes.string,
  timezone: PropTypes.string,
  temperature: PropTypes.string,
  profile_picture: PropTypes.string,
  country: PropTypes.string,
  paging_size: PropTypes.number,
  note_switch: PropTypes.bool,
  notification_switch: PropTypes.bool,
  alerts_switch: PropTypes.bool,
  accepted_last_data_privacy: PropTypes.bool,
  accepted_last_privacy_policy: PropTypes.bool,
  consent_newsletter: PropTypes.bool,
  consent_personal_data_usage: PropTypes.bool,
};

const location = PropTypes.shape({
  hash: PropTypes.string.isRequired,
  key: PropTypes.string, // only in createBrowserHistory and createMemoryHistory
  pathname: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  state: PropTypes.oneOfType([PropTypes.array, PropTypes.bool, PropTypes.number, PropTypes.object, PropTypes.string]), // only in createBrowserHistory and createMemoryHistory
});

const history = PropTypes.shape({
  action: PropTypes.oneOf(['PUSH', 'REPLACE', 'POP']).isRequired,
  block: PropTypes.func.isRequired,
  canGo: PropTypes.func, // only in createMemoryHistory
  createHref: PropTypes.func.isRequired,
  entries: PropTypes.arrayOf(location), // only in createMemoryHistory
  go: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goForward: PropTypes.func.isRequired,
  index: PropTypes.number, // only in createMemoryHistory
  length: PropTypes.number,
  listen: PropTypes.func.isRequired,
  location: location.isRequired,
  push: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
});

const match = PropTypes.shape({
  isExact: PropTypes.bool,
  params: PropTypes.object.isRequired,
  path: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
});

export const rdi = PropTypes.shape({
  data: PropTypes.arrayOf(PropTypes.shape as any),
  id: PropTypes.number,
  name: PropTypes.string,
});

export const temperatureHumidity = {
  id: PropTypes.number,
  data: PropTypes.shape({
    humidity: PropTypes.arrayOf(PropTypes.shape as any),
    temperature: PropTypes.arrayOf(PropTypes.shape as any),
  }),
  name: PropTypes.string,
};

export const airspacesStatuses = {
  id: PropTypes.number,
  name: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape as any),
  devices: PropTypes.arrayOf(PropTypes.shape as any),
};

export const RouterPropTypes = {
  location,
  history,
  match,
};

export const rhs = PropTypes.shape({
  id: PropTypes.number,
  fullname: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape as any),
});

export const vetHistory = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  sensor: PropTypes.oneOf([0, 1, 2]),
  devices: PropTypes.arrayOf(PropTypes.shape as any),
});

export const logbookEvent = PropTypes.shape({
  datetime: PropTypes.any,
  type: PropTypes.string,
  value: PropTypes.shape({}),
});

export const logbookItem = PropTypes.shape({
  date: PropTypes.any,
  events: PropTypes.arrayOf(logbookEvent),
});

export const colorHistory = PropTypes.shape({
  date: PropTypes.any,
  color: PropTypes.string,
  real_color: PropTypes.string,
  status: PropTypes.string,
  real_status: PropTypes.string,
});
